import type { ShopReasonsDto } from "@babackapp/private-api-client/types";
import useTranslation from "@babackapp/translations/use-translation";
import { ExclamationTriangleIcon, GlobeIcon } from "@radix-ui/react-icons";
import { useCurrentShopLocalesAvailables } from "@/lib/translator";
import useTextDtoValue from "@/utils/use-text-dto-value";
import UpdateReturnReasonDialog from "./update-return-reason-dialog";

type ReturnReasonListItemProps = {
	reason: ShopReasonsDto;
};

export default function ReturnReasonListItem({
	reason,
}: ReturnReasonListItemProps) {
	const t = useTranslation();
	const languages = useCurrentShopLocalesAvailables();

	const missingLanguages = languages.filter((lang) => {
		const value = reason.values.find((v) => v.lang === lang.code);
		return !value || value.text.trim().length === 0;
	});

	const label = useTextDtoValue(reason.values);

	const badges = [
		{
			content: t("settings.reasons.badges.photoAllowed"),
			display:
				reason.rules.photoRequired.available &&
				!reason.rules.photoRequired.required,
		},
		{
			content: t("settings.reasons.badges.photoRequired"),
			display: reason.rules.photoRequired.required,
		},
		{
			content: t("settings.reasons.badges.commentRequired"),
			display: reason.rules.comment.available && reason.rules.comment.required,
		},
		{
			content: t("settings.reasons.badges.commentAllowed"),
			display: reason.rules.comment.available && !reason.rules.comment.required,
		},
		{
			content: t("settings.reasons.badges.skipReturnCost"),
			display: reason.rules.skipReturnCost.available,
		},
		{
			content: t("settings.reasons.badges.skipReshipping"),
			display: reason.rules.skipReshipping.available,
		},
		{
			content: t("settings.reasons.badges.highlighted"),
			display: reason.rules.highlighted.available,
		},
		{
			content: t("settings.reasons.badges.reservedForLoggedUser"),
			display: reason.rules.reservedForLoggedUser.available,
		},
	].filter((badge) => badge.display);

	return (
		<div className="border-b border-border last:border-b-0">
			<UpdateReturnReasonDialog reason={reason}>
				<button
					role="button"
					className="flex flex-row py-2 justify-start items-center relative group h-auto px-2 my-2 rounded-lg bg-transparent hover:bg-muted whitespace-normal w-full gap-4"
				>
					<GlobeIcon className="size-4 text-muted-foreground" />
					<div className="flex h-full text-left flex-col items-start w-full justify-center py-2">
						<p className="text-base font-medium leading-none tracking-tight text-left">
							{label}
						</p>
						{badges.length || missingLanguages?.length ? (
							<span className="inline-flex gap-2 flex-wrap mt-2">
								{badges.map((badge) => (
									<ReturnReasonBadge
										key={badge.content}
										content={badge.content}
									/>
								))}
								{missingLanguages?.map((lang) => (
									<ReturnReasonWarning
										key={lang.code}
										content={t("settings.reasons.missingLanguage", {
											language: lang.name,
										})}
									/>
								))}
							</span>
						) : null}
					</div>
				</button>
			</UpdateReturnReasonDialog>
		</div>
	);
}

type ReturnReasonBadgeProps = {
	content: string;
};

function ReturnReasonBadge({ content }: ReturnReasonBadgeProps) {
	return (
		<span className="font-medium px-2 py-1 rounded-full border-border border text-xs">
			{content}
		</span>
	);
}

function ReturnReasonWarning({ content }: ReturnReasonBadgeProps) {
	return (
		<span className="font-medium px-2 py-1 rounded-full border-destructive border text-xs gap-2 inline-flex items-center text-destructive">
			<ExclamationTriangleIcon className="size-4" />
			<span>{content}</span>
		</span>
	);
}
